import { Env, h } from '@stencil/core';
import { endpoints } from '../../.conf/resources';

import type {
  EnergyEfficiency,
  EnergyEfficiencyScale,
  ImageParams,
  PreviewErrors,
  Product,
  ResponsiveImages,
  Teaser,
  YouTubeVideo,
} from './utils/interfaces';

const videoPoster = 'video-poster-webp-750x420.webp';

const iconWrapperStyles = {
  maxWidth: '25%',
  width: '25%',
  height: 'auto',
  position: 'var(--cms-play-icon-position)',
  transform: 'var(--cms-play-icon-transform)',
  top: 'var(--cms-play-icon-top)',
  left: 'var(--cms-play-icon-left)',
  opacity: 'var(--cms-play-icon-opacity)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

export const getTenant = () => Env.tenantEnv || 'hornbach';

export const addClass = (node: HTMLElement, className: string) => {
  node.classList.add(className);
};

export const removeClass = (node: HTMLElement, className: string) => {
  if (hasClass(node, className)) {
    node.classList.remove(className);
  }
};

export const hasClass = (node: HTMLElement, className: string) => {
  return node.classList.contains(className);
};

export const addAttribute = (node: HTMLElement, attr: string) => {
  node.setAttribute(attr, 'true');
};

export const removeAttribute = (node: HTMLElement, attr: string) => {
  node.removeAttribute(attr);
};

export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
};

// TODO maybe can delete this function we hav a component for this
export const renderYouTubeVideo = (youTubeVideo: YouTubeVideo, id?: string) => {
  const youTubeVideoStyles = {
    aspectRatio: '1.5 / 1',
    width: '100%',
  };

  if (typeof id !== 'undefined') {
    id = uniqueId();
  }

  // To acivate hdp set data-hdp-src on componentDidLoad
  return (
    <iframe
      id={id}
      style={youTubeVideoStyles}
      title={youTubeVideo.title}
      loading='lazy'
      class={`lazyload ${youTubeVideo.class}`}
      allowFullScreen
      src={youTubeVideo.preview ? youTubeVideo.src : null}
      data-use-hdp={!youTubeVideo.preview ? 'true' : null}
      data-usercentrics={!youTubeVideo.preview ? 'YouTube Video' : null}
    />
  );
};

export const uniqueId = (): string => {
  //generates random id;
  const generatePart = () => {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  };
  return `${generatePart()}-${generatePart()}`;
};

export const renderResponsiveYoutubeImages = (
  responsiveImages: ResponsiveImages,
  sizes: string,
  altAndTitle: string,
  cssClass: string,
) => {
  const wrapperStyles = {
    position: 'relative',
    height: '100%',
  };
  const youtubeImageStyles = {
    display: 'block',
    width: '100%',
    height: '100%',
    aspectRatio: '1.5 / 1',
    backgroundColor: '#000',
  };

  return (
    <div style={wrapperStyles} class={'youtube-image-wrapper'}>
      <picture class={'image-wrapper'} style={youtubeImageStyles}>
        <source
          type='image/webp'
          sizes={sizes}
          srcSet={`${responsiveImages.bp320} 320w,
          ${responsiveImages.bp480} 480w,
         ${responsiveImages.bp768} 768w, 
        ${responsiveImages.bp992} 992w,
        ${responsiveImages.bp1200} 1200w`}
        />
        <img
          loading='lazy'
          style={youtubeImageStyles}
          alt={altAndTitle}
          title={altAndTitle}
          class={cssClass}
          src={responsiveImages.jpg992Default}
          width='992'
          height='661'
        />
      </picture>
      <div style={iconWrapperStyles} class={'play-icon-wrapper'}>
        <cms-icon size='xxl' icon='youtubePlay' />
      </div>
    </div>
  );
};

export const renderResponsiveImages2 = (imageParams: ImageParams) => {
  const imageStyles = {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: 'contain',
    objectPosition: 'top',
  };

  if (!imageParams.responsiveImages) {
    return;
  }

  return (
    <picture
      style={imageParams.imageStyles ? imageParams.imageStyles : imageStyles}
      class={'teaser-picture'}
    >
      <source
        type='image/webp'
        sizes={imageParams.imageSizes ? imageParams.imageSizes : null}
        srcSet={`${imageParams.responsiveImages.bp320} 320w,
          ${imageParams.responsiveImages.bp480} 480w,
         ${imageParams.responsiveImages.bp768} 768w,
        ${imageParams.responsiveImages.bp992} 992w,
        ${imageParams.responsiveImages.bp1200} 1200w`}
      />
      <img
        loading={imageParams.lazyLoad ? 'lazy' : null}
        style={imageParams.imageStyles ? imageParams.imageStyles : imageStyles}
        alt={imageParams.altText || ''}
        class={imageParams.imageClass || 'teaser-image'}
        src={
          imageParams.responsiveImages
            ? imageParams?.responsiveImages?.jpg992Default
            : null
        }
        width='992'
        height='661'
      />
    </picture>
  );
};

export const renderResponsiveImages = (
  responsiveImages: ResponsiveImages,
  sizes: string,
  altText: string,
  cssClass: string,
  paramImageStyles?: { [key: string]: string },
) => {
  const imageStyles = {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: 'cover',
    objectPosition: 'var(--cms-image-object-position)',
  };

  return (
    <picture style={paramImageStyles || imageStyles} class={'teaser-picture'}>
      <source
        type='image/webp'
        sizes={sizes}
        srcSet={`${responsiveImages.bp320} 320w,
          ${responsiveImages.bp480} 480w,
         ${responsiveImages.bp768} 768w, 
        ${responsiveImages.bp992} 992w,
        ${responsiveImages.bp1200} 1200w`}
      />
      <img
        loading='lazy'
        style={paramImageStyles || imageStyles}
        alt={altText}
        class={cssClass}
        src={responsiveImages.jpg992Default}
        width='992'
        height='661'
      />
    </picture>
  );
};

export const renderArticleImg = (altText: string, imgSrc: string) => {
  const imageStyle = {
    width: '100%',
    height: 'auto',
  };
  return (
    <img
      loading='lazy'
      style={imageStyle}
      alt={altText}
      title={altText}
      width='500'
      height='400'
      src={imgSrc}
      class='article-image'
    />
  );
};

/* EEK */
export const renderArticleEnergyLabels = (
  definitions: EnergyEfficiency[],
  datasheetLabel: string,
  datasheetAccessibleName?: string,
  size?: string,
): HTMLElement[] =>
  definitions.map((efficiency, i, list) => {
    if (i === -1) {
      /* Only required because build requires 'i' to be used */
      console.error('Invalid index');
    }
    return renderArticleEnergyLabel(
      efficiency.efficiencyClass,
      efficiency.rank,
      efficiency.label,
      efficiency.labelVersion,
      list.length > 0,
      efficiency.scale,
      efficiency.dataSheet,
      datasheetLabel,
      datasheetAccessibleName,
      efficiency.name,
      size,
    );
  });

export const renderArticleEnergyLabel = (
  energyEfficiencyClass: string,
  energyEfficiencyRank: string | number,
  energyEfficiencyLabelUrl: string,
  energyEfficiencyVersion: string,
  multi,
  scale: EnergyEfficiencyScale,
  datasheetUrl?: string,
  datasheetLabel?: string,
  datasheetAccessibleName?: string,
  name?: string,
  size?: string,
): HTMLElement => (
  <cms-energy-label
    energyLabel={energyEfficiencyClass}
    energyRank={energyEfficiencyRank.toString()}
    energyImgUrl={energyEfficiencyLabelUrl}
    energyVersion={energyEfficiencyVersion}
    datasheetUrl={datasheetUrl}
    datasheetLabel={datasheetLabel}
    datasheetAccessibleName={datasheetAccessibleName}
    name={name}
    multi={multi}
    scale={scale}
    size={size}
  />
);

export const renderEnergyLabels = (
  {
    energyEfficiency,
    energyEfficiencyClass,
    energyEfficiencyRank,
    energyEfficiencyLabelUrl,
    energyEfficiencyVersion,
    datasheetLabel,
    datasheetUrl,
  }: Product,
  datasheetAccessibleName?: string,
  size?: string,
): HTMLElement => {
  if (energyEfficiency) {
    const wrapperStyle = {
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--cms-spacing-025)',
      marginBottom: 'var(--cms-spacing-075)',
    };
    return (
      <div class='energy-label-wrapper' style={wrapperStyle}>
        {renderArticleEnergyLabels(
          energyEfficiency,
          datasheetLabel,
          datasheetAccessibleName,
          size,
        )}
      </div>
    );
  }
  if (
    energyEfficiencyClass &&
    energyEfficiencyRank &&
    energyEfficiencyVersion
  ) {
    return renderArticleEnergyLabel(
      energyEfficiencyClass,
      energyEfficiencyRank,
      energyEfficiencyLabelUrl,
      energyEfficiencyVersion,
      false,
      null,
      datasheetUrl,
      datasheetLabel,
      datasheetAccessibleName,
    );
  }
  return;
};

//TODO refactor: make parameter more generic like an object
export const renderVideoPoster = (teaser: Teaser): HTMLElement => {
  const { headline } = teaser;
  const posterWrapperStyles = {
    position: 'relative',
  };
  const posterStyles = {
    display: 'block',
    width: '100%',
    height: '100%',
    aspectRatio: '1.5 / 1',
  };

  return (
    <div class={'poster-wrapper'} style={posterWrapperStyles}>
      <img
        style={posterStyles}
        loading='lazy'
        alt={headline}
        title={headline}
        class='video-poster'
        src={getAssetSrc(videoPoster)}
        width='750'
        height='420'
      />
      <div class={'play-icon-wrapper'} style={iconWrapperStyles}>
        <cms-icon size='xxl' icon='controlPlay' />
      </div>
    </div>
  );
};

//TODO refactor: make parameter more generic like an object
export const renderYoutubeImages = (
  teaser: Teaser,
  imageSizes: string,
): ResponsiveImages => {
  // Remove filename from thumbnailUrl
  const thumbnailBaseUrl = teaser.youtubeThumbnail.substring(
    0,
    teaser.youtubeThumbnail.lastIndexOf('/'),
  );
  if (!thumbnailBaseUrl) {
    return {} as ResponsiveImages;
  }
  // Make sure, that webp is used
  const thumbnailBaseUrlWebp = thumbnailBaseUrl.replace('/vi/', '/vi_webp/');
  const youtubeImages: ResponsiveImages = {
    bp320: `${thumbnailBaseUrlWebp}/mqdefault.webp`,
    bp480: `${thumbnailBaseUrlWebp}/hqdefault.webp`,
    bp768: `${thumbnailBaseUrlWebp}/sddefault.webp`,
    bp992: `${thumbnailBaseUrlWebp}/sddefault.webp`,
    bp1200: `${thumbnailBaseUrlWebp}/sddefault.webp`,
    jpg992Default: `${thumbnailBaseUrl}/sddefault.jpg`,
  };

  return renderResponsiveYoutubeImages(
    youtubeImages,
    imageSizes,
    teaser.headline,
    'youtube-poster',
  );
};

export const getTeaserText = (
  text: string,
  moreTeaserLabel: string,
): string => {
  const maxLength = 220;
  const decodedText = decodeHtmlEntities(text.trim());

  /* Replace all HTML Tags - special handling for cms-link required, cause content is not rendered inline */
  const textWithoutParagraphWrapper = decodedText.replaceAll(
    /<\/?p[^>]*>/gm,
    '',
  );
  const textWithoutHtml = textWithoutParagraphWrapper
    .replaceAll(/<cms-link[^>]*content=["'‘]([^"'‘]*)["'‘][^>]?>/gm, '$1')
    .replaceAll(/<\/?\w+[^>]*>/gm, '');

  /* Remove surrounding paragraphs */
  if (textWithoutHtml.length <= maxLength) {
    return `<p style="margin-top:0;">${textWithoutParagraphWrapper}</p>`;
  }
  const truncatedText = trimTextWithHtml(
    textWithoutParagraphWrapper,
    textWithoutHtml,
    maxLength,
  );

  return `<p style="margin-top:0;">${truncatedText}... <span class="more-text" style="text-decoration: underline">${moreTeaserLabel}</span></p>`;
};

const trimTextWithHtml = (
  text: string,
  textWithoutHtml: string,
  maxLength: number,
): string => {
  if (text.indexOf('<') > -1) {
    return textWithoutHtml.substring(0, maxLength);
  }
  return text.substring(0, maxLength);
};

const decodeHtmlEntities = (text: string): string => {
  return text
    .replace(/&amp;/g, '&')
    .replace(/&#(\d+);/g, (_match, dec) =>
      String.fromCharCode(Number.parseInt(dec, 10)),
    );
};

export const getAssetSrc = (image: string): string => {
  if (image !== undefined) {
    return image ? `${endpoints.assetPath}/${image}` : '';
  }
};

export const articleHasEnergyLabel = ({
  energyEfficiency,
  energyEfficiencyClass,
  energyEfficiencyRank,
  energyEfficiencyVersion,
}: Product): boolean => {
  return (
    (energyEfficiency && energyEfficiency.length > 0) ||
    !!(energyEfficiencyClass && energyEfficiencyRank && energyEfficiencyVersion)
  );
};

export const renderStandardTeaserErrorMessages = (
  previewErrors: PreviewErrors,
) => {
  const {
    noKeyvisual,
    anotherRegion,
    invalid_container,
    noPageSelected,
    missingContent,
    emptyElement,
    isOffline,
  } = previewErrors;
  return (
    <div class='error-container'>
      <ul class='error-list'>
        {noKeyvisual === '' ? null : (
          <li class='error-item' innerHTML={noKeyvisual} />
        )}
        {anotherRegion === '' ? null : (
          <li class='error-item' innerHTML={anotherRegion} />
        )}
        {invalid_container === '' ? null : (
          <li class='error-item' innerHTML={invalid_container} />
        )}
        {noPageSelected === '' ? null : (
          <li class='error-item' innerHTML={noPageSelected} />
        )}
        {missingContent === '' ? null : (
          <li class='error-item' innerHTML={missingContent} />
        )}
        {emptyElement === '' ? null : (
          <li class='error-item' innerHTML={emptyElement} />
        )}
        {isOffline === '' ? null : (
          <li class='error-item' innerHTML={isOffline} />
        )}
      </ul>
    </div>
  );
};

export const getYoutubeVideoId = (youTubeParam: string) => {
  const splitParam = youTubeParam?.split('&')[0].split('?');
  return splitParam[0];
};

export const isEnterKey = (evt: KeyboardEvent) => {
  return evt.key === 'Enter' || evt.key === ' ';
};

export const calculateEnergyEfficiencyImagePath = (
  cdnBaseUrl: string,
  efficiencyScale: EnergyEfficiencyScale,
  efficiencyClass: string,
) => {
  const cacheBusterPostfix = 'v1';
  return new URL(
    `/webshop-commons/images/energyefficiency/${encodeURIComponent(
      `Class_Arrows_${efficiencyScale.highest}${efficiencyScale.lowest}_${efficiencyClass}.${cacheBusterPostfix}`,
    )}.svg`,
    cdnBaseUrl,
  ).toString();
};

export const openUrl = (url: string) => {
  const win = window.open(url, '_self');
  if (win != null) {
    win.focus();
  }
};
